import { cn } from '@/lib/utils'
import { type ChangeEvent, type KeyboardEvent, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { useFieldContext } from '@/lib/form-contexts'
import { Ban } from 'lucide-react'
import { useDebouncedAction } from '@/hooks/useDebouncedAction'
import { isColorValid, normalizeHexColor } from '@/lib/storefront-theme'

export type ColorPickerProps = {
  disabled?: boolean
  className?: string
}

function ColorPicker({ disabled = false, className }: ColorPickerProps) {
  const field = useFieldContext<string | null | undefined>()

  const debouncedHandleChange = useDebouncedAction(field.handleChange, 10)

  const colorPickerRef = useRef<HTMLInputElement>(null)

  const isValidColor = isColorValid(field?.state?.value)
  const displayColorValue =
    isValidColor && typeof field?.state?.value === 'string'
      ? normalizeHexColor(field?.state?.value)
      : '#000000'

  // this is keyboard input, no biggie, doesn't need to be debounced
  // it's for the display, not the underlying input
  const handleHexInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value?.replaceAll('#', '')
    field.handleChange(`#${newValue}`)
  }

  const handleHexInputBlur = () => {
    const normalizedValue = normalizeHexColor(field?.state?.value)

    if (isColorValid(normalizedValue) && normalizedValue !== field?.state?.value) {
      field.handleChange(normalizedValue)
    }

    field.handleBlur?.()
  }

  // close the color picker on enter
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget?.blur()
    }
  }

  // open the native color picker from our fake color picker
  const handleSwatchClick = () => {
    if (!colorPickerRef.current) {
      return
    }

    if (typeof colorPickerRef.current.showPicker === 'function') {
      colorPickerRef.current.showPicker()
      return
    }

    colorPickerRef.current.click()
  }

  return (
    <div className={cn('flex gap-2 items-center', className)}>
      <button
        type="button"
        data-raw-button-owner="native-color-swatch"
        aria-label="Open color picker"
        className={cn(
          'relative size-7 scale-120 rounded-md shrink-0 transition-transform hover:scale-125 flex items-center justify-center shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          disabled && 'opacity-50'
        )}
        style={{ backgroundColor: !isValidColor ? '#aaaaaa' : displayColorValue }}
        onClick={disabled ? undefined : handleSwatchClick}
        disabled={disabled}
      >
        {!isValidColor && <Ban className="size-4 text-muted-foreground" />}
        {/*
            Keep the native color input on top of the visible swatch so the browser
            anchors its picker to the button the florist actually clicked.
        */}
        <input
          ref={colorPickerRef}
          type="color"
          value={displayColorValue}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          className="pointer-events-none absolute inset-0 h-full w-full cursor-pointer opacity-0"
          disabled={disabled}
          tabIndex={-1}
        />
      </button>

      <Input
        value={field?.state?.value ?? ''}
        onChange={handleHexInputChange}
        onBlur={handleHexInputBlur}
        onKeyDown={handleKeyDown}
        placeholder="#FFBB00"
        size="xs"
        className="flex-1 tabular-nums"
        disabled={disabled}
      />
    </div>
  )
}

export default ColorPicker
